<template>
  <div class="vacancy-edit-page">
    <BasePreloader v-if="pendingLoadSettings" />
    <div v-else class="vacancy-edit-page__content">
      <h1 class="vacancy-edit-page__title">
        {{ $tNewVacancy2('Create vacancy') }}
      </h1>
      <div class="vacancy-edit-page__content-inner">
        <div ref="main"
             class="vacancy-edit-page__main">
          <div class="accordion vacancy-edit-page__accordion">
            <div ref="section-1"
                 :class="showSection === 1 ? '_open' : null"
                 class="accordion__item">
              <div @click.prevent="onToggle(1)"
                   class="accordion__head">
                <h3 class="accordion__name">
                  {{ $tNewVacancy2('Main information') }}
                </h3>
                <div class="accordion__btn">
                  <BaseSvg name="ico-arrow-down-2"/>
                </div>
              </div>
              <collapse-transition
                :duration="showSection === 1 ? durationShow : durationHide"
                @after-enter="scrollToSectionInputs(1)"
              >
                <EditSectionBasic
                  v-show="showSection === 1"
                  :key="$route.fullPath"
                  class="accordion__content"
                  ref="editSection1"
                  :selected-group-id="currentGroupsId"
                  @change-selected-group-id="updateCurrentGroupId"
                />
              </collapse-transition>
            </div>
            <div ref="section-2"
                 :class="showSection === 2 ? '_open' : null"
                 class="accordion__item">
              <div @click.prevent="onToggle(2)"
                   class="accordion__head">
                <h3 class="accordion__name">
                  {{ $tNewVacancy2('requirement for candidate') }}
                </h3>
                <div class="accordion__btn">
                  <BaseSvg name="ico-arrow-down-2"/>
                </div>
              </div>
              <collapse-transition
                :duration="showSection === 2 ? durationShow : durationHide"
                @after-enter="scrollToSectionInputs(2)"
              >
                <EditRequirementsCandidate
                  v-show="showSection === 2"
                  :key="$route.fullPath"
                  class="accordion__content"
                  ref="editSection2"
                  :vacancy-created="vacancyCreated"
                  @click:disable-cover="scrollToInvalidFields"
                />
              </collapse-transition>
            </div>
            <div ref="section-3"
                 :class="showSection === 3 ? '_open' : null"
                 class="accordion__item">
              <div @click.prevent="onToggle(3)"
                   class="accordion__head">
                <h3 class="accordion__name">
                  {{ $tNewVacancy2('Description') }}
                </h3>
                <div class="accordion__btn">
                  <BaseSvg name="ico-arrow-down-2"/>
                </div>
              </div>
              <collapse-transition
                :duration="showSection === 3 ? durationShow : durationHide"
                @after-enter="scrollToSectionInputs(3)"
              >
                <EditDescription
                  v-show="showSection === 3"
                  :key="$route.fullPath"
                  class="accordion__content"
                  ref="editSection3"
                  :vacancy-created="vacancyCreated"
                  @click:disable-cover="scrollToInvalidFields"
                />
              </collapse-transition>
            </div>
            <div ref="section-4"
                 :class="showSection === 4 ? '_open' : null"
                 class="accordion__item">
              <div @click.prevent="onToggle(4)"
                   class="accordion__head">
                <h3 class="accordion__name">
                  {{ $tNewVacancy2('additional_questions') }}
                </h3>
                <div class="accordion__btn">
                  <BaseSvg name="ico-arrow-down-2"/>
                </div>
              </div>
              <collapse-transition
                :duration="showSection === 4 ? durationShow : durationHide"
                @after-enter="scrollToSectionInputs(4)"
              >
                <EditAdditionalQuestions
                  v-show="showSection === 4"
                  :key="$route.fullPath"
                  class="accordion__content"
                  ref="editSection4"
                  :vacancy-created="vacancyCreated"
                  @click:disable-cover="scrollToInvalidFields"
                />
              </collapse-transition>
            </div>
          </div>

          <div v-if="isOurEmployer" class="pl-1 pt-7">
            <SwitchCheckbox
              v-model="forceTranslate"
              :label="$tNewVacancy2('automatically_translate_vacancy_with_Google')"
              class="_blue"
            />
          </div>

          <section class="vacancy-edit-page__footer">
            <button
              class="vacancy-edit-page__save  base-btn  base-btn--outline-grey  base-btn--hover-blue"
              @click.prevent="onSaveVacancyClick(false)"
            >
              <BasePreloader v-if="pendingSave1"/>
              <span :style="{opacity: pendingSave1 ? 0 : null}">{{ $tNewVacancy2('Save') }}</span>
            </button>

            <button
              class="vacancy-edit-page__save  _with-post  base-btn  base-btn--blue"
              @click.prevent="onSaveVacancyClick(true)"
            >
              <BaseSvg
                name="ico-check"
                width="1.4rem"
                height="1.8rem"
                :style="pendingSave2 ? 'opacity: 0' : null"
              />
              <BasePreloader v-if="pendingSave2" />
              <span :style="{opacity: pendingSave2 ? 0 : null}">
                {{ $tNewVacancy2('Save and publish') }}
              </span>
            </button>
          </section>
        </div>
        <div v-if="!isFixed"
             ref="side"
             class="vacancy-edit-page__side">
          <StickyContainer class="vacancy-edit-page__side-sticky">
            <div class="vacancy-edit-page__side-inner">
              <VacancyEditFilledProgress ref="filledProgress" class="vacancy-edit-page__side-item" @click:list-item="onProgressCheckItemClick" />
              <VacancyEditSideHelpTooltips v-if="hints"
                                           :currentId="currentGroupsId"
                                           :hints="hints"
                                           class="vacancy-edit-page__side-item"/>
            </div>
          </StickyContainer>
        </div>
      </div>
      <template v-if="isFixed">
        <VacancyEditFilledProgress ref="filledProgress" is-fixed @click:list-item="onProgressCheckItemClick" />
        <VacancyEditSideHelpTooltips v-if="hints"
                                     :currentId="currentGroupsId"
                                     :hints="hints"
                                     is-fixed/>
      </template>
    </div>
  </div>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex';
  import store from '../../store'

  const storeModule = () => import(/* webpackChunkName: "account-page" */ '../../store/vacancyEdit')
  import {CollapseTransition} from 'vue2-transitions'
  import StickyContainer from '../../components/StickyContainer'
  import windowSizeMixin from '../../mixins/windowSizeMixin'
  import VacancyEditFilledProgress from '../../components/vacancyEdit/VacancyEditFilledProgress'
  import VacancyEditSideHelpTooltips from '../../components/vacancyEdit/VacancyEditSideHelpTooltips'
  import SwitchCheckbox from '../../components/SwitchCheckbox'

  import EditSectionBasic from '../../components/vacancyEdit/EditSectionBasic'
  import EditRequirementsCandidate from '../../components/vacancyEdit/EditRequirementsCandidate'
  import EditDescription from '../../components/vacancyEdit/EditDescription'
  import EditAdditionalQuestions from '../../components/vacancyEdit/EditAdditionalQuestions'

  const isHTMLElementRef = ref => ref instanceof HTMLElement;

  export default {
    mixins: [windowSizeMixin],
    components: {
      EditDescription,
      EditAdditionalQuestions,
      CollapseTransition,
      EditSectionBasic,
      StickyContainer,
      SwitchCheckbox,
      VacancyEditFilledProgress,
      VacancyEditSideHelpTooltips,
      EditRequirementsCandidate
    },
    async beforeRouteEnter(to, from, next) {
      const module = await storeModule();

      if (store.state.vacancyEdit) {
        await store.dispatch('vacancyEdit/resetState');
      } else {
        store.registerModule('vacancyEdit', module.default);
      }

      await store.dispatch('vacancyEdit/setStaticLists');
      await store.dispatch('vacancyEdit/settingsLoad', {
        vacancyId: to.query.vacancy || to.query.duplicate,
        duplicate: !!to.query.duplicate,
      });

      next();
    },
    async beforeRouteUpdate(to, from, next) {
      const toVacancyId = this.$get(to.query, 'vacancy');
      const fromVacancyId = this.$get(from.query, 'vacancy');
      const fromDuplicateVacancyId = this.$get(from.query, 'duplicate');

      if (toVacancyId !== fromVacancyId && !fromDuplicateVacancyId) {
        if (!this.justCreated) {
          await store.dispatch('vacancyEdit/resetState');
        }
        await store.dispatch('vacancyEdit/setStaticLists');
        await store.dispatch('vacancyEdit/settingsLoad', {
          vacancyId: toVacancyId,
          disablePending: true,
        });
      }

      if (this.justCreated) {
        await store.dispatch('vacancyEdit/setDefaultJustCreated');
        this.useDefaultSectionScroll = false;
      }

      next();
    },
    beforeRouteLeave(to, from, next) {
      if (store.state.vacancyEdit) {
        store.dispatch('vacancyEdit/resetState');
      }

      next();
    },
    data: () => ({
      showSection: 1,
      currentGroupsId: 0,
      timer: null,
      durationShow: 600,
      durationHide: 400,
      forceTranslate: false,
      hints: null,

      useDefaultSectionScroll: true,
    }),
    computed: {
      ...mapGetters('vacancyEdit', [
        'savedBlocks',
        'justCreated',
        'afterJustCreated',
        'allInputs',
      ]),
      ...mapGetters('user', [
        'isOurEmployer',
      ]),

      pendingLoadSettings() {
        return !this.$store.state.vacancyEdit || this.$store.state.vacancyEdit.pendingLoadSettings
      },
      pendingSave1() {
        return !this.$store.state.vacancyEdit || this.$store.state.vacancyEdit.pendingSave1
      },
      pendingSave2() {
        return !this.$store.state.vacancyEdit || this.$store.state.vacancyEdit.pendingSave2
      },
      isFixed() {
        return this.windowSizeMixin.width <= process.env.VUE_APP_WINDOW_SIZE_MD
      },
      triggerPosition() {
        return this.windowSizeMixin.height / 100 * 50
      },
      isVacancy() {
        return !!this.$route.query.vacancy
      },
      inputsHasErrors() {
        return Object.values(this.allInputs).some(v => v.error);
      },
      firstInvalidInputEntry() {
        return Object.entries(this.allInputs).find(([, input]) => input.error);
      },
      vacancyCreated() {
        return this.savedBlocks?.[1] === true;
      },
    },
    created() {
      this.loadHints();
    },
    mounted() {
      this.checker()
      window.addEventListener('scroll', this.checker)
    },
    beforeDestroy() {
      window.removeEventListener('scroll', this.checker)
    },
    methods: {
      ...mapActions([
        'getHints'
      ]),
      ...mapActions('helpers', [
        'canDoAction',
      ]),
      ...mapActions('vacancyEdit', [
        'saveVacancy',
        'getLanguagesPopup',
      ]),
      ...mapActions('helpers', [
        'canDoAction',
      ]),

      async onSaveVacancyClick(publish) {
        if (
          this.inputsHasErrors || this.firstInvalidInputEntry || (publish && Object.values(this.savedBlocks).some(b => !b))) {
          this.scrollToInvalidFields();
          return;
        }

        const params = { publish, forceTranslate: undefined };
        if (this.isOurEmployer && (this.forceTranslate || (this.afterJustCreated && !this.forceTranslate))) {
          const popupsData = await this.getLanguagesPopup();
          if (!popupsData) return;

          params.forceTranslate = this.forceTranslate;

          const { title, text: message } = popupsData[params.forceTranslate ? 'popup_2' : 'popup_1'];

          this.$vModal.open('confirm', {
            title,
            message,
            successBtnClass: 'base-btn--outline-blue',
            onAccept: () => this.saveVacancyIfPossible(params),
          });

          return;
        }

        this.saveVacancyIfPossible(params);
      },
      async saveVacancyIfPossible(params) {
        const canSave = await this.canDoAction({ action: 'action_publication' });

        if (!canSave.data) {
          this.$vModal.open('confirm', {
            title: canSave.popup?.title,
            message: canSave.popup?.text,
            successBtnClass: 'base-btn--outline-blue',
            onSuccess: () => {
              this.saveVacancy(params);
            },
          });

          return;
        }

        this.saveVacancy(params);
      },

      setCurrentGroupId(id) {
        this.currentGroupsId = id;
      },
      updateCurrentGroupId(id) {
        this.setCurrentGroupId(id);
        this.closeCheckList();
      },
      closeCheckList() {
        const { filledProgress } = this.$refs;
        if (filledProgress.isOpenCheckList) filledProgress.isOpenCheckList = false;
      },
      async loadHints() {
        const hints = await this.getHints({ group: 'edit_create_vacancy' }).catch(() => {});
        if (hints) {
          this.hints = hints;
        }
      },
      checker() {
        clearTimeout(this.timer)

        this.timer = setTimeout(() => {
          const $main = this.$refs['main']
          if (!$main) return
          const $groups = $main.querySelectorAll('[data-edit-group]')
          const triggerPosition = this.triggerPosition


          for (const $group of $groups) {
            const boundingClientRect = $group.getBoundingClientRect()

            if (boundingClientRect.top < triggerPosition && triggerPosition < boundingClientRect.bottom) {
              const newId = Number($group.getAttribute('data-edit-group'));
              return this.setCurrentGroupId(newId);
            }
          }

          this.currentGroupsId = null
        }, 10)
      },
      getSectionWithChildRef(refName) {
        const sectionsEntries = Object.entries(this.$refs).filter(([key]) => key.startsWith('editSection'));
        return sectionsEntries.find(([, component]) => component.$refs[refName]) || [];
      },
      onToggle(value) {
        if (value === this.showSection) {
          this.showSection = null
        } else {
          // this.showSection = value
          this.showSection = null
          setTimeout(() => {
            this.showSection = value
          }, 100)
        }
      },
      scrollToSectionInputs(section) {
        if (!this.useDefaultSectionScroll) return;

        setTimeout(() => {
          this.$refs['section-' + section].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      },
      scrollToInvalidFields() {
        const [key] = this.firstInvalidInputEntry || [];
        if (key) {
          for (const sectionNumber of [1, 2, 3, 4]) {
            const refTarget = this.$refs['editSection' + sectionNumber].$refs['input-' + key];
            if (!refTarget) continue;

            this.useDefaultSectionScroll = false;
            this.showSection = sectionNumber;

            setTimeout(() => {
              this.scrollToRefTarget(refTarget, { block: 'center' });
              this.useDefaultSectionScroll = true;
            }, 700);
          }
        }
      },
      async onProgressCheckItemClick(inputRef) {
        try {
          const data = await this.scrollToField(inputRef, { block: 'center' });
          setTimeout(() => {
            this.openDisabledInput(inputRef, data);
          }, 200);
        } catch {
          console.error('Failed to scroll');
        }
      },
      scrollToField(inputRef, scrollData) {
        return new Promise((resolve, reject) => {
          const [sectionKey, refSectionTarget] = this.getSectionWithChildRef(inputRef);
          if (sectionKey) {
            const newVisibleSection = Number(sectionKey[sectionKey.length - 1]);
            const timeout = this.showSection === newVisibleSection ? 0 : 700;

            this.useDefaultSectionScroll = false;
            this.showSection = newVisibleSection;

            setTimeout(() => {
              this.scrollToRefTarget(refSectionTarget.$refs[inputRef], scrollData);
              this.useDefaultSectionScroll = true;

              resolve({
                inputRef,
                sectionKey,
                refSectionTarget,
                newVisibleSection,
              });
            }, timeout);
          } else reject();
        });
      },
      openDisabledInput(inputRef, data) {
        const key = inputRef?.split('input-').join('');

        let storeSection = null;

        switch (this.showSection) {
          case 1: storeSection = 'sectionBase'; break;
          case 2: storeSection = 'sectionRequirements'; break;
          case 3: storeSection = 'sectionDescription'; break;
          default: storeSection = null;
        }

        const isCustomDisabledInput = this.checkCustomDisabledInput(key, data);
        if (isCustomDisabledInput) return;

        if (this.allInputs[key]?.disabled) {
          this.$store.commit(`vacancyEdit/${storeSection}/setInputDisabled`, [key, false])
        }
      },
      checkCustomDisabledInput(key, data) {
        if (key.startsWith('conditions')) {
          data.refSectionTarget.$refs[data.inputRef].open();
          return true;
        }
        return false;
      },
      scrollToRefTarget(refTarget, scrollParams = { behavior: 'smooth', block: 'center' }) {
        if (isHTMLElementRef(refTarget)) {
          refTarget.scrollIntoView(scrollParams);
        } else refTarget.$el.scrollIntoView(scrollParams);
      },
    },
  }
</script>
